import { ReactNode } from "react";
import {
  BiBell,
  BiCloudUpload,
  BiGroup,
  BiLayer,
  BiQrScan,
  BiReceipt,
  BiRevision,
  BiWifiOff,
} from "react-icons/bi";
import { ReactComponent as SquareLogo } from "../../assets/svg/Square_Logo.svg";

const Tile = ({
  icon,
  title,
  description,
  className = "",
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  className?: string;
  children?: ReactNode;
}) => (
  <div
    className={`group flex flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(15,23,42,0.35)] ${className}`}
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
      {icon}
    </span>
    <h3 className="mt-5 text-lg font-semibold text-slate-900">{title}</h3>
    <p className="mt-2 leading-7 text-slate-600">{description}</p>
    {children}
  </div>
);

const FeatureBento = () => (
  <section id="features" className="bg-white px-5 py-24 md:px-8 md:py-32">
    <div className="mx-auto max-w-6xl">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
          Features
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
          Built for real event days, not spreadsheets.
        </h2>
        <p className="mt-4 text-lg leading-8 text-slate-600">
          The details that matter when the line is ten deep.
        </p>
      </div>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {/* Square: the big tile */}
        <div className="relative isolate flex flex-col overflow-hidden rounded-3xl bg-[#070b17] p-8 text-white md:col-span-2 lg:row-span-2">
          <div
            aria-hidden
            className="absolute -right-24 -top-24 -z-10 h-80 w-80 rounded-full bg-blue-600/30 blur-[90px]"
          />
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white">
            <SquareLogo className="h-7 w-7" />
          </span>
          <h3 className="mt-6 text-2xl font-semibold tracking-tight md:text-3xl">
            Tap to Pay and card readers with Square
          </h3>
          <p className="mt-3 max-w-md leading-7 text-slate-400">
            Connect your Square account and take cards on your phone or a
            Square reader, right inside checkout. Every sale lands on the right
            event. Included on every plan, even Free.
          </p>
          <div className="relative mx-auto mt-10 flex h-48 w-48 items-center justify-center lg:mt-auto">
            <span className="vo-ripple absolute inset-0 rounded-full border-2 border-blue-400/60" />
            <span className="vo-ripple-delayed absolute inset-0 rounded-full border-2 border-violet-400/60" />
            <span className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-center shadow-[0_0_60px_rgba(99,102,241,0.6)]">
              <span className="text-xs font-medium text-blue-100">Total</span>
              <span className="text-xl font-bold">$61.48</span>
              <span className="mt-0.5 text-[10px] uppercase tracking-wider text-blue-100">
                Tap here
              </span>
            </span>
          </div>
        </div>

        <Tile
          icon={<BiWifiOff size={22} />}
          title="Keep selling with no signal"
          description="Keep recording cash and QR code payments when the signal drops. They’re saved on your phone and sync when you’re back online. Square card payments need a connection."
          className="md:col-span-2"
        />
        <Tile
          icon={<BiLayer size={22} />}
          title="Inventory pools"
          description="Keep each show’s stock separate and move items between them on purpose."
        />
        <Tile
          icon={<BiGroup size={22} />}
          title="Staff pay"
          description="Hourly, per-event, or commission pay, with refunds already taken out."
        />
        <Tile
          icon={<BiReceipt size={22} />}
          title="Sales tax reports"
          description="Monthly reports by jurisdiction, with Square sales matched to the right event and a CSV ready for your accountant."
          className="md:col-span-2"
        />
        <Tile
          icon={<BiQrScan size={22} />}
          title="Your QR codes"
          description="Show your own payment QR at checkout."
        >
          <div className="mt-4 flex flex-wrap gap-1.5">
            {["Venmo", "Cash App", "Zelle", "PayPal"].map((name) => (
              <span
                key={name}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
              >
                {name}
              </span>
            ))}
          </div>
        </Tile>
        <Tile
          icon={<BiBell size={22} />}
          title="Low-stock alerts"
          description="Set a threshold per item and hear about it before the best seller sells out."
        />
        <Tile
          icon={<BiRevision size={22} />}
          title="Refunds and exchanges"
          description="Record returns and exchanges at the booth, and inventory, totals, and commissions update on their own. Card and QR payments are refunded in Square or the payment app."
          className="md:col-span-2"
        />
        <Tile
          icon={<BiCloudUpload size={22} />}
          title="Bulk import"
          description="Bring in your whole catalog from Excel or a Square export instead of typing it in."
          className="md:col-span-2"
        />
      </div>
    </div>
  </section>
);

export default FeatureBento;
